import React, { useCallback, useMemo, useRef, useState } from 'react';

const COLLECTIBLE_DRAWER_SWIPE_THRESHOLD_PX = 24;
const COLLECTIBLE_DRAWER_SWIPE_MAX_MS = 450;

/**
 * Mobile-only wrapper: collapses map collectible meters into a right-edge drawer
 * opened/closed by horizontal swipe (or tap on the handle).
 */
export default function ExploreUnknownMobileCollectibleDrawer({ isMobile, tabLabel, children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const swipeRef = useRef(null);
  const swipeConsumedRef = useRef(false);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    swipeRef.current = {
      x: event.clientX,
      y: event.clientY,
      time: Date.now(),
      pointerId: event.pointerId,
    };
    swipeConsumedRef.current = false;
  }, []);

  const finalizeSwipe = useCallback((event) => {
    const start = swipeRef.current;
    if (!start || start.pointerId !== event.pointerId) return;

    swipeRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const elapsed = Date.now() - start.time;
    if (elapsed > COLLECTIBLE_DRAWER_SWIPE_MAX_MS) return;
    if (Math.abs(deltaX) < COLLECTIBLE_DRAWER_SWIPE_THRESHOLD_PX) return;
    if (Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    swipeConsumedRef.current = true;
    if (deltaX < 0) {
      setDrawerOpen(true);
      return;
    }
    setDrawerOpen(false);
  }, []);

  const handlePointerUp = useCallback((event) => finalizeSwipe(event), [finalizeSwipe]);

  const handlePointerCancel = useCallback((event) => {
    if (swipeRef.current?.pointerId === event.pointerId) {
      swipeRef.current = null;
    }
  }, []);

  const handleTabClick = useCallback(() => {
    if (swipeConsumedRef.current) {
      swipeConsumedRef.current = false;
      return;
    }
    setDrawerOpen((open) => !open);
  }, []);

  const swipeHandlers = useMemo(
    () =>
      isMobile
        ? {
            onPointerDown: handlePointerDown,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerCancel,
          }
        : {},
    [handlePointerCancel, handlePointerDown, handlePointerUp, isMobile]
  );

  if (!isMobile) {
    return <div className="explore-unknown-map-collectible-meters">{children}</div>;
  }

  return (
    <div
      className={`eu-collectible-mobile-drawer${drawerOpen ? ' eu-collectible-mobile-drawer--open' : ''}`}
    >
      <aside
        id="eu-collectible-mobile-drawer-panel"
        className="eu-collectible-mobile-drawer-panel"
        aria-label={tabLabel}
        {...swipeHandlers}
      >
        <button
          type="button"
          className="eu-collectible-mobile-drawer-tab"
          aria-expanded={drawerOpen}
          aria-controls="eu-collectible-mobile-drawer-scroll"
          onClick={handleTabClick}
        >
          <span className="eu-collectible-mobile-drawer-tab-label">{tabLabel}</span>
        </button>
        <div
          id="eu-collectible-mobile-drawer-scroll"
          className="eu-collectible-mobile-drawer-scroll"
          aria-hidden={!drawerOpen}
        >
          <div className="explore-unknown-map-collectible-meters explore-unknown-map-collectible-meters--mobile-in-drawer">
            {children}
          </div>
        </div>
      </aside>
    </div>
  );
}
